import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { combineLatest, startWith, map } from 'rxjs';
import { UserDetails } from '#models';
import { UserRole } from '#enums';
import { AuthService } from './auth/auth.service';

interface NavTreeItemType {
  roles: UserRole[];
  routerLink?: string;
  subItems?: NavTreeType[];
}
type NavTreeType = Record<string, NavTreeItemType>;

@Injectable({
  providedIn: 'root',
})
export class NavbarService {
  private _authService = inject(AuthService);
  private _translateService = inject(TranslateService);

  // Nav tree — top navigation bar items
  private readonly navTree: NavTreeType = {
    jobManagement: {
      roles: [UserRole.User],
      routerLink: '/job-management',
    },
    allOpenCases: {
      roles: [UserRole.User],
      routerLink: '/all-open-cases',
    },
    userManagement: {
      roles: [UserRole.Admin],
      routerLink: '/admin/users',
    },
    admin: {
      roles: [UserRole.Admin],
      subItems: [
        {
          adminUsers: {
            roles: [UserRole.Admin],
            routerLink: '/admin/users',
          },
          adminNotificationPreferences: {
            roles: [UserRole.Admin],
            routerLink: '/admin/notification-preferences',
          },
        },
      ],
    },
  };

  getNavigation() {
    return combineLatest([
      this._translateService.onLangChange.pipe(
        startWith(this._translateService.getCurrentLang()),
      ),
      this._authService.userDetails$.pipe(startWith(null)),
    ]).pipe(
      map(([_, userDetails]) =>
        this._getNavigationItems(userDetails, this.navTree),
      ),
    );
  }

  private _getNavigationItems(
    userDetails: UserDetails | null,
    navTree: NavTreeType,
  ): MenuItem[] {
    if (!userDetails) {
      // Show items anyway for mockup — treat unauthenticated as basic user
      userDetails = {
        email: '',
        username: '',
        role: '',
        isAdmin: false,
        hasAccess: true,
      };
    }

    const menuItems: MenuItem[] = [];

    const nav = Object.entries(navTree);
    for (const [key, value] of nav) {
      const { roles = [], routerLink = '', subItems = [] } = value;
      if (this._authService.isAuthorized(roles)) {
        const item: MenuItem = {
          label: this._translateService.instant(this._parseNavItemLabel(key)),
          routerLink: routerLink,
        };

        for (const subItem of subItems) {
          item.items = this._getNavigationItems(userDetails, subItem);
        }

        menuItems.push(item);
      }
    }

    return menuItems;
  }

  private _parseNavItemLabel(key: string): string {
    return 'navigation.' + key;
  }
}
